import "react-responsive-carousel/lib/styles/carousel.min.css"; // requires a loader
import { Carousel } from "react-responsive-carousel";
import {
  BathRoomIconRent,
  BedIconRent,
  FavoriteIconRent,
  GuestsIcon,
  HeartIconRent,
  NUSDIcon,
  PinIcon,
  UnHeartIconRent,
} from "../../AssetComponents/Images";
import { PurpleButton } from "../Buttons/PurpleButton";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect, memo, useMemo, useCallback, useRef } from "react";
import { api } from "../functions/Api";
import { setConnectModal } from "../../ReduxSlices/ModalSlice";
import { Mainnet } from "@nibiruchain/nibijs";
import { updateToken, addToken } from "../functions/Functions";
import { normalizePricePerDay } from "../../utils/propertyValidation";
import { PropertyImageWithFallback } from "../Images/PropertyImageWithFallback";
import { updateNFTStore } from "../../ReduxSlices/NFTSlice";

const renderCustomPrevArrow = (onClickHandler, hasPrev, label) => {
  return (
    <div
      onClick={onClickHandler}
      disabled={!hasPrev}
      className="custom-arrow custom-prev-arrow p-1 rounded-full bg-[#ffffff80] hover:bg-[#ffffff]"
    >
      <svg
        width="12"
        height="12"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M3.75 9H14.25"
          stroke="#323232"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M7.5 12.75L3.75 9"
          stroke="#323232"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M7.5 5.25L3.75 9"
          stroke="#323232"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
  );
};

// Custom render function for next arrow
const renderCustomNextArrow = (onClickHandler, hasNext, label) => {
  return (
    <div
      onClick={onClickHandler}
      disabled={!hasNext}
      className="custom-arrow custom-next-arrow p-1 rounded-full bg-[#ffffff80] hover:bg-[#ffffff]"
    >
      <svg
        width="12"
        height="12"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M14.25 9H3.75"
          stroke="#323232"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M10.5 12.75L14.25 9"
          stroke="#323232"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M10.5 5.25L14.25 9"
          stroke="#323232"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
  );
};

// Custom render function for indicators - limit to max 5 dots
const renderCustomIndicator = (onClickHandler, isSelected, index, label) => {
  // Only render indicators for the first 5 images (index 0-4)
  if (index >= 5) {
    return null;
  }
  
  return (
    <li
      className={`control-dot ${isSelected ? 'selected' : ''}`}
      onClick={onClickHandler}
      aria-label={label}
      role="button"
      tabIndex={0}
    />
  );
};

export const MiniRentalItem = memo(({
  token_id,
  tokenData,
  onClick,
  showArrow = true,
  confetti = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [avgRate, setAvgRate] = useState();
  const token = useSelector((state) => state.nft.nfts[token_id]);
  const account = useSelector((state) => state.auth.account);
  const [wishlisted, setWishlisted] = useState(false);
  const [status, setStatus] = useState();
  const diff = useSelector((state) => state.time.diffToUTC);
  const dispatch = useDispatch();
  const mainnet = Mainnet();
  const [isWishlistLoading, setIsWishlistLoading] = useState(false);
  // 🚀 FIX: Use ref to prevent duplicate simultaneous requests
  const wishlistRequestInFlight = useRef(false);

  // Memoized price calculation - normalize to handle both raw and converted formats
  const pricePerDay = useMemo(() => {
    return normalizePricePerDay((token || tokenData)?.short?.price_per_day || 0);
  }, [token, tokenData]);

  // Memoized property data
  const propertyData = useMemo(() => {
    return token || tokenData;
  }, [token, tokenData]);

  const getToken = async () => {
    if (!token) {
      try {
        const propertyData = await api("property/getProperty", {
          token_id: token_id,
        });
        if (propertyData) {
          dispatch(
            updateNFTStore({
              token_id: token_id,
              metaData: propertyData.metaData,
              short: propertyData.short,
              rentals: propertyData.rentals,
              access: propertyData.access,
              icalSync: propertyData.icalSync || null,
              manuallyBlockedDates: propertyData.manuallyBlockedDates || [],
            })
          );
        }
      } catch (error) {
        console.error("Failed to get property from backend:", error);
        await updateToken(
          {
            token_id: token_id,
            contract: process.env.REACT_APP_RENTAL_SMART_CONTRACT,
          },
          mainnet.endptTm,
          dispatch
        );
      }
    }
  };

  const decideStatus = async () => {
    if (!token) return;
    const currentTime = new Date().getTime() + diff;
    const startTime = new Date(token?.short?.start_time).getTime();
    const endTime = new Date(token?.short?.end_time).getTime();

    if (currentTime < startTime) {
      setStatus(0); // Not started
    } else if (currentTime >= startTime && currentTime <= endTime) {
      setStatus(1); // Active
    } else {
      setStatus(2); // Ended
    }
  };

  const handleWishlist = useCallback(async () => {
    if (!account) {
      console.log('⚠️  No account connected, showing connect modal');
      dispatch(setConnectModal(true));
      return;
    }
    
    // 🚀 FIX: Prevent duplicate simultaneous requests
    if (wishlistRequestInFlight.current) {
      console.log("⏸️  Wishlist request already in flight, ignoring duplicate click");
      return;
    }
    
    console.log('🔄 Toggle wishlist:', { 
      account: account.substring(0, 10) + '...', 
      token_id, 
      currentState: wishlisted 
    });
    
    // Mark request as in flight
    wishlistRequestInFlight.current = true;
    setIsWishlistLoading(true);
    
    try {
      // Use correct endpoint: user/addToWishlist (same as RentalItem)
      const res = await api("user/addToWishlist", {
        account: account,
        token_id: token_id,
      });
      
      console.log('✅ Wishlist toggle response:', { token_id, newState: res });
      
      if (res !== undefined) {
        // Update state based on response (true = in wishlist, false = not in wishlist)
        setWishlisted(res);
        
        // 🚀 FIX: Dispatch event to refresh wishlist in dashboard
        window.dispatchEvent(new CustomEvent('wishlistUpdated', {
          detail: { token_id, inWishlist: res }
        }));
      }
    } catch (error) {
      console.error("❌ Wishlist operation failed:", error);
      // On error, re-check the wishlist status
      if (account && token_id) {
        try {
          console.log('🔄 Re-checking wishlist status after error...');
          const checkRes = await api("user/checkWishlist", {
            account: account,
            token_id: token_id,
          });
          console.log('📋 Re-checked wishlist status:', { token_id, isWishlisted: !!checkRes });
          setWishlisted(!!checkRes);
        } catch (checkError) {
          console.error("❌ Failed to re-check wishlist status:", checkError);
        }
      }
    } finally {
      setIsWishlistLoading(false);
      // Reset the in-flight flag after a short delay to prevent rapid clicks
      setTimeout(() => {
        wishlistRequestInFlight.current = false;
      }, 300);
    }
  }, [account, dispatch, token_id, wishlisted]);

  useEffect(() => {
    getToken();
  }, [token_id]);

  useEffect(() => {
    decideStatus();
  }, [token, diff]);

  const checkWishlist = useCallback(async () => {
    if (!account || !token_id) return;
    try {
      console.log('🔍 Checking wishlist status:', { account: account.substring(0, 10) + '...', token_id });
      // Use correct endpoint: user/checkWishlist (same as RentalItem)
      const res = await api("user/checkWishlist", {
        account: account,
        token_id: token_id,
      });
      console.log('📋 Wishlist status received:', { token_id, isWishlisted: !!res });
      setWishlisted(!!res); // Ensure boolean value
    } catch (error) {
      console.error("❌ Failed to check wishlist status:", error);
      // Don't update state on error - keep current state
    }
  }, [account, token_id]);

  useEffect(() => {
    if (account && token_id) {
      checkWishlist();
    }
  }, [token_id, account, checkWishlist]);

  // Fetch average rating - handle gracefully if endpoint doesn't exist
  useEffect(() => {
    const fetchRating = async () => {
      try {
        const response = await api("rating/average", {
          token_id: token_id,
        });
        if (response && response.averageRating) {
          setAvgRate(response.averageRating.toFixed(1));
        }
      } catch (error) {
        // Silently handle 404 or other errors - rating is optional
        console.log("Rating endpoint not available, skipping rating fetch");
        setAvgRate(null);
      }
    };

    if (token_id) {
      fetchRating();
    }
  }, [token_id]);

  return (
    <div
      className="hover:bg-[#AAAAAA30] relative group bg-white w-full max-w-[180px] min-w-[144px] rounded-[8px] h-max p-[6px] shadow-[-2px_-2px_6px_0px_rgba(253,255,255,0.8),2px_2px_6px_0px_rgba(187,195,206,0.6)] opacity-0 animate-fadeIn"
      style={{ animationFillMode: 'forwards' }}
    >
      <Carousel
        showStatus={false}
        showArrows={showArrow}
        showThumbs={false}
        renderArrowPrev={renderCustomPrevArrow}
        renderArrowNext={renderCustomNextArrow}
        renderIndicator={renderCustomIndicator}
      >
        {(token || tokenData)?.metaData?.images && (token || tokenData)?.metaData.images.length > 0 ? 
          (token || tokenData)?.metaData.images.map((image, i) => {
            return (
              <div
                className="h-[108px] rounded-[6px] relative"
                key={i}
              >
                <div className="w-full h-full absolute group-hover:bg-[#00000030] top-0 left-0 rounded-[6px]"></div>

                <PropertyImageWithFallback
                  src={image}
                  alt={`Property image ${i + 1}`}
                  className="h-[108px] rounded-[6px] w-full object-cover"
                  priority={i === 0} // First image gets priority loading
                  lazy={i > 0} // Lazy load subsequent images
                  width={400}
                  height={108}
                  quality={i === 0 ? 85 : 75} // Higher quality for first image
                  progressive={true}
                />
              </div>
            );
          }) : (
            <div className="h-[108px] rounded-[6px] relative bg-gray-200 flex items-center justify-center">
              <div className="text-gray-500 text-center">
                <div className="text-2xl mb-1">🏠</div>
                <div className="text-xs">No images</div>
              </div>
            </div>
          )
        }
      </Carousel>

      {/* Heart Icon */}
      <div
        onClick={(e) => {
          e.stopPropagation();
          if (!isWishlistLoading) {
            handleWishlist();
          }
        }}
        className="absolute top-[10px] right-[10px] z-10 cursor-pointer"
      >
        {isWishlistLoading ? (
          // Simple spinner during loading
          <div className="animate-spin w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full"></div>
        ) : wishlisted ? (
          <HeartIconRent className="w-[20px] h-[20px]" />
        ) : (
          <UnHeartIconRent className="w-[20px] h-[20px]" />
        )}
      </div>

      {/* Price and Star */}
      <div className="flex items-center justify-between mt-[8px]">
        <div className="flex items-center gap-[4px]">
          <NUSDIcon className="w-[12px] h-[12px]" />
          <div className="text-[12px] font-semibold text-[#202020]">
            {pricePerDay} USDC /night
          </div>
        </div>
        <div className="flex items-center gap-[4px]">
          <div className="p-[3px] w-max rounded-full shadow-[-2px_-2px_6px_0px_rgba(253,255,255,0.80),2px_2px_6px_0px_rgba(187,195,206,0.60)]">
            <FavoriteIconRent className="w-[8px] h-[8px]" />
          </div>
          <div className="text-[10px] text-[#959595]">{avgRate || ''}</div>
        </div>
      </div>

      {/* Location */}
      <div className="flex items-center gap-[4px] mt-[4px]">
        <PinIcon className="w-[10px] h-[10px]" />
        <div className="text-[10px] text-[#959595] truncate">
          {(token || tokenData)?.metaData?.addressString || "/NA"}
        </div>
      </div>

      {/* Amenities */}
      <div className="flex items-center gap-[6px] w-full mt-[8px]">
        <div className="flex items-center gap-[4px]">
          <div className="p-[3px] w-max rounded-full shadow-[-2px_-2px_6px_0px_rgba(253,255,255,0.80),2px_2px_6px_0px_rgba(187,195,206,0.60)]">
            <GuestsIcon className="w-[8px] h-[8px]" />
          </div>
          <div className="text-[10px] text-[#959595]">
            {(token || tokenData)?.metaData?.essentials?.guests || 0}
          </div>
        </div>
        <div className="w-[1px] h-[12px] bg-[#959595]"></div>
        <div className="flex items-center gap-[4px]">
          <div className="p-[3px] w-max rounded-full shadow-[-2px_-2px_6px_0px_rgba(253,255,255,0.80),2px_2px_6px_0px_rgba(187,195,206,0.60)]">
            <BathRoomIconRent className="w-[8px] h-[8px]" />
          </div>
          <div className="text-[10px] text-[#959595]">
            {(token || tokenData)?.metaData?.essentials?.bathrooms || 0}
          </div>
        </div>
        <div className="w-[1px] h-[12px] bg-[#959595]"></div>
        <div className="flex items-center gap-[4px]">
          <div className="p-[3px] w-max rounded-full shadow-[-2px_-2px_6px_0px_rgba(253,255,255,0.80),2px_2px_6px_0px_rgba(187,195,206,0.60)]">
            <BedIconRent className="w-[8px] h-[8px]" />
          </div>
          <div className="text-[10px] text-[#959595]">
            {(token || tokenData)?.metaData?.essentials?.beds || 0}
          </div>
        </div>
        <div className="w-[1px] h-[12px] bg-[#959595]"></div>
        <div className="flex items-center gap-[4px]">
          <div className="p-[3px] w-max rounded-full shadow-[-2px_-2px_6px_0px_rgba(253,255,255,0.80),2px_2px_6px_0px_rgba(187,195,206,0.60)]">
            <svg
              width="10"
              height="10"
              viewBox="0 0 17 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12 7.33333H14.6667V9.99999C13.1939 9.99999 12 8.80609 12 7.33333Z"
                stroke="#202020"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M11.3337 2C13.1746 2 14.667 3.49238 14.667 5.33333V10.6667C14.667 12.5076 13.1746 14 11.3337 14H6.00033C4.15938 14 2.66699 12.5076 2.66699 10.6667V5.33333C2.66699 3.49239 4.15938 2 6.00033 2H11.3337Z"
                stroke="#202020"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8.66732 10.6667V14"
                stroke="#202020"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8.67122 7.33333V2"
                stroke="#202020"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M5.33366 7.33333H2.66699"
                stroke="#323232"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="text-[10px] text-[#959595]">
            {(token || tokenData)?.metaData?.essentials?.square || 0}
          </div>
          <div className="text-[10px] text-[#959595] items-top flex">
            <span>M</span>
            <span className="text-[8px] top-0">2</span>
          </div>
        </div>
      </div>

      {/* View Details Button */}
      <div className="mt-[8px]">
        <PurpleButton
          text={
            <div
              className="text-center text-[10px] py-[4px]"
              onClick={
                onClick
                  ? onClick
                  : () => {
                      navigate(location.pathname + "/" + token_id);
                    }
              }
            >
              View Details
            </div>
          }
        />
      </div>
    </div>
  );
});

// Add display name for debugging
MiniRentalItem.displayName = 'MiniRentalItem';
